import type React from "react";
import { useState } from "react";
import type { DiaryFormData } from "@/types/diary";

interface DiaryFormProps {
	initialData?: DiaryFormData;
	onSubmit: (formData: DiaryFormData) => Promise<void>;
}

const DiaryForm: React.FC<DiaryFormProps> = ({ initialData, onSubmit }) => {
	const [formData, setFormData] = useState<DiaryFormData>(
		initialData || {
			title: "",
			content: "",
			date: new Date().toISOString().split("T")[0],
		},
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setError(null);

		try {
			await onSubmit(formData);
		} catch (err) {
			setError(err as Error);
			console.error("日記の保存に失敗しました:", err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			{error && (
				<div className="text-red-500 text-sm mb-4">
					エラーが発生しました: {error.message}
				</div>
			)}
			<div>
				<label htmlFor="date" className="block mb-1">
					日付
				</label>
				<input
					type="date"
					id="date"
					name="date"
					value={formData.date}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
					required
				/>
			</div>
			<div>
				<label htmlFor="title" className="block mb-1">
					タイトル
				</label>
				<input
					type="text"
					id="title"
					name="title"
					value={formData.title}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
					required
				/>
			</div>
			<div>
				<label htmlFor="content" className="block mb-1">
					内容
				</label>
				<textarea
					id="content"
					name="content"
					value={formData.content}
					onChange={handleChange}
					className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
					rows={10}
					required
				/>
			</div>
			<button
				type="submit"
				className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
				disabled={loading}
			>
				{loading ? "保存中..." : initialData ? "更新" : "作成"}
			</button>
		</form>
	);
};

export default DiaryForm;
